<template>
  <div class="flex h-full flex-col gap-2 overflow-hidden">
    <div class="flex-1 min-h-0 overflow-y-auto">
      <div v-if="subscriptions.length > 1" class="sticky top-0 z-20 bg-white pb-1 pt-2">
        <InfuseSegments>
          <InfuseSegment
            :active="activeSubscription?.id === subscription.id"
            v-for="subscription in subscriptions"
            :key="subscription.id"
            :name="subscription.name"
            :label="() => subscription.name"
            @click="activeSubscription = subscription"
          />
        </InfuseSegments>
      </div>

      <div class="pb-[4rem]">
        <PermissionsSelector
          :permissions="permissionsPerCategoryFlat.filter((p) => p.subscriptionId === activeSubscription?.id)"
          :enabled-permissions="enabledPermissions"
          :loading="isFetching"
          :search-query="searchQuery"
          :multiple-segments-in-slide-in="subscriptions.length > 1"
          @add="addPermission"
          @remove="removePermission"
          @clear="onClearCategory"
          @readOnly="onReadOnly"
          @all="onAllPermissions"
          @update:searchQuery="debounceSearch"
        >
          <template #actions>
            <InfuseButton
              v-permission="{ id: exportPermission, hide: false }"
              data-testid="role-export-button"
              color="secondary"
              :loading="isExporting"
              :disabled="isExporting"
              @click="onExportRole"
            >
              <font-awesome-icon :icon="['far', 'arrow-down-to-line']" class="mr-2" />
              {{ t('iam.roles.header.export_role') }}
            </InfuseButton>

            <InfuseButton
              v-permission="{ id: importPermission, hide: false }"
              data-testid="role-import-button"
              color="secondary"
              @click="showImportModal = true"
            >
              <font-awesome-icon :icon="['far', 'arrow-up-from-line']" class="mr-2" />
              {{ t('iam.roles.header.import_role') }}
            </InfuseButton>

            <InfuseButton @click="onSavePermissions" :loading="isMutating" :disabled="isMutating">
              {{ t('action.save') }}
            </InfuseButton>
          </template>
        </PermissionsSelector>
      </div>
    </div>
  </div>

  <ImportRolesModal
    v-if="showImportModal"
    :single-role-only="true"
    :report="importReport"
    :is-previewing="isPreviewingImport"
    :is-importing="isImportingRoles"
    @close="closeImportModal"
    @preview="onPreviewImport"
    @confirm="onConfirmImport"
  />
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { AccessType } from '@infuse/api/iam/permission';
import { organisationRolePermissions } from '@infuse/api/iam/organisation';
import { rolePermissions as domainRolePermissions } from '@infuse/api/iam/role';
import { tenantRolePermissions } from '@infuse/api/iam/tenant';
import { useAssetLevel, usePermissionsCategories, usePermissionsSubscriptionFilter } from '@infuse/iam/composables';
import { vPermission } from '@infuse/iam/permission/directives';
import { usePermissions } from '@infuse/iam/permission/queries';
import { PermissionsSelector } from '@infuse/iam/permission/ui';
import { useAddPermissionToRole, useExportRoles, useImportRoles, usePreviewRoleImport } from '@infuse/iam/role/mutations';
import { useRolePermissions } from '@infuse/iam/role/queries';
import { Role, RoleImportReport, RoleTransfer } from '@infuse/iam/role/type';
import { InfuseButton, InfuseSegment, InfuseSegments } from '@infuse/ui/components';
import { debounce } from 'lodash';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getRoleUrlParameters, ImportRolesModal } from '@infuse/iam/role/ui';

const { t } = useI18n();

const props = defineProps<{
  model: Role;
  isLoading?: boolean;
}>();

const { getLevel } = useAssetLevel();

const level = computed(() => getLevel(props.model));
const parentId = computed(() => props.model.organisationId || props.model.tenantId);

const {
  permissions: rolePermissions,
  fetchRoleId,
  isLoading: isLoadingRolePermissions,
  levelType,
  levelId
} = useRolePermissions();

const { permissions, isLoading: isLoadingPermissions, searchQuery } = usePermissions(level.value, parentId.value);

const { subscriptions, activeSubscription } = usePermissionsSubscriptionFilter(permissions);
const { getPermissionsOfCategory, permissionsPerCategory } = usePermissionsCategories(permissions);

const { mutateAsync: addPermissionsToRole, isPending: isAddingPermissions } = useAddPermissionToRole(
  level.value,
  parentId.value
);

const { mutateAsync: exportRoles, isPending: isExporting } = useExportRoles(level.value, parentId.value);
const { mutateAsync: previewImport, isPending: isPreviewingImport } = usePreviewRoleImport(level.value, parentId.value);
const { mutateAsync: importRoles, isPending: isImportingRoles } = useImportRoles(level.value, parentId.value);

const showImportModal = ref(false);
const importReport = ref<RoleImportReport | undefined>(undefined);

// Export and import of a single role sit behind their own permission, separate from the one that
// allows handling several roles at once from the role list.
const exportPermission = computed(() => permissionFor('EXPORT'));
const importPermission = computed(() => permissionFor('IMPORT'));

function permissionFor(action: 'EXPORT' | 'IMPORT'): string {
  switch (level.value) {
    case 'organisation':
      return organisationRolePermissions[action];
    case 'tenant':
      return tenantRolePermissions[action];
    default:
      return domainRolePermissions[action];
  }
}

async function onExportRole(): Promise<void> {
  await exportRoles({ roleIds: [props.model.id] });
}

async function onPreviewImport(roles: RoleTransfer[]): Promise<void> {
  importReport.value = await previewImport({ roles });
}

async function onConfirmImport(roles: RoleTransfer[]): Promise<void> {
  await importRoles({ roles });
  closeImportModal();
}

function closeImportModal(): void {
  showImportModal.value = false;
  importReport.value = undefined;
}

const isFetching = computed(() => props.isLoading || isLoadingRolePermissions.value || isLoadingPermissions.value);
const isMutating = computed(() => isAddingPermissions.value);

watch(
  () => props.model,
  (role) => {
    if (!role) {
      return;
    }

    const roleUrlParams = getRoleUrlParameters(role);
    levelType.value = roleUrlParams?.level;
    levelId.value = roleUrlParams?.levelId;

    fetchRoleId.value = role.id;
  },
  { immediate: true }
);

watch(
  () => rolePermissions.value,
  () => {
    for (const permission of rolePermissions.value) {
      addPermission(permission.id);
    }
  },
  { immediate: true }
);

const debounceSearch = debounce((query: string) => {
  if (query !== searchQuery.value) {
    searchQuery.value = query;
  }
}, 500);

let enabledPermissions = reactive<string[]>([]);

function addPermission(permissionId: string) {
  if (enabledPermissions.includes(permissionId)) {
    return;
  }

  enabledPermissions.push(permissionId);
}

function removePermission(permissionId: string) {
  const index = enabledPermissions.indexOf(permissionId);
  if (index > -1) {
    enabledPermissions.splice(index, 1);
  }
}

function onClearCategory(categoryId: string) {
  const categoryPermissions = getPermissionsOfCategory(categoryId);

  for (const categoryPermission of categoryPermissions) {
    removePermission(categoryPermission.id);
  }
}

function onReadOnly(categoryId: string) {
  onClearCategory(categoryId);
  const categoryPermissions = getPermissionsOfCategory(categoryId).filter(
    (p) => p.accessType === AccessType.READ || p.accessType === AccessType.SEARCH
  );

  for (const categoryPermission of categoryPermissions) {
    addPermission(categoryPermission.id);
  }
}

function onAllPermissions(categoryId: string) {
  const categoryPermissions = getPermissionsOfCategory(categoryId);

  for (const categoryPermission of categoryPermissions) {
    addPermission(categoryPermission.id);
  }
}

async function onSavePermissions() {
  await addPermissionsToRole({ roleId: props.model.id, permissionIds: enabledPermissions, replace: true });
}

const permissionsPerCategoryFlat = computed(() => {
  // Flatten all permissions for PermissionsSelector
  return permissionsPerCategory.value.flatMap((cat) => [
    ...cat.objectTypeActions.flatMap((a) => a.actions),
    ...cat.objectTypeCrud.flatMap((c) => c.crud)
  ]);
});
</script>
